import React, {useEffect, useContext, useState} from 'react';
import './Users.scss';
import {I18nContext} from '../../contexts/i18nContext';
import {AdminContext} from '../../contexts/adminContext';
import {Button, Loader, Modal} from '../_common';
import UserRow from './UserRow';
import UserForm from './UserForm';
import ChangePasswordForm from './ChangePasswordForm';
import ArrowLeftIcon from '@mui/icons-material/ArrowLeft';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import ConfirmationModal from "../_common/ConfirmationModal";

const Users = () => {
  const { t } = useContext(I18nContext);
  const {
    pending,
    users,
    getUsers,
    paginationInfo,
    addUser,
    updateUser,
    deleteUser,
    changePasswordPending,
    changePasswordByAdmin
  } = useContext(AdminContext);
  const [page, setPage] = useState(0);
  const [jumpToPageInput, setJumpToPageInput] = useState('');
  const [newUserData, setNewUserData] = useState({});
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [showDeleteUserModal, setShowDeleteUserModal] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [currentUser, setCurrentUser] = useState({});
  const [emailError, setEmailError] = useState(false);

  useEffect(() => {
    if (!!paginationInfo && paginationInfo.number !== page) {
      setPage(paginationInfo.number);
    }
  }, [paginationInfo]);

  useEffect(() => {
    if (page || page === 0) {
      getUsers(10, page);
    }
  }, [page]);

  const rolesOptions = [
    {id: 'ADMIN', label: t('admin')},
    {id: 'USER', label: t('user')}
  ];

  const changePage = () => {
    if (
      jumpToPageInput !== paginationInfo.number + 1 &&
      jumpToPageInput <= paginationInfo.totalPages &&
      jumpToPageInput > 0)
    {
      setPage(jumpToPageInput - 1);
      setJumpToPageInput('');
    }
  };

  const handleChangePageOnEnterPress = e => {
    if (e.keyCode === 13) {
      changePage();
    }
  };

  const changePageUp = () => {
    if (page < paginationInfo.totalPages - 1) {
      setPage(page + 1);
    }
  };

  const changePageDown = () => {
    if (page > 0) {
      setPage(page - 1);
    }
  };

  const updateNewUserData = formValues => {
    setNewUserData(formValues);
  };

  const toggleShowAddUserModal = () => {
    setShowAddUserModal(!showAddUserModal);
    setEmailError(false);
  };

  const addNewUser = async () => {
    const newUser = await addUser(newUserData);
    if (newUser.status === 403) {
      setEmailError(true);
    } else {
      toggleShowAddUserModal();
      getUsers(10, page);
      setNewUserData({});
    }
  };

  const toggleShowEditUserModal = () => {
    setShowEditUserModal(!showEditUserModal);
  };

  const handleEditUserClick = user => {
    setCurrentUser(user);
    setNewUserData(user);
    toggleShowEditUserModal();
  };

  const editUser = async () => {
    const editedUser = await updateUser(newUserData, currentUser.id);
    getUsers(10, page);
    setNewUserData({});
    setCurrentUser({});
    toggleShowEditUserModal();
  };

  const toggleShowDeleteUserModal = () => {
    setShowDeleteUserModal(!showDeleteUserModal);
  };

  const handleDeleteUserClick = async user => {
    setCurrentUser(user);
    toggleShowDeleteUserModal();
  };

  const removeUser = async () => {
    const deletedUser = await deleteUser(currentUser.id);
    getUsers(10, 0);
    setNewUserData({});
    setCurrentUser({});
    toggleShowDeleteUserModal();
  };

  const toggleShowChangePasswordModal = () => {
    setShowChangePasswordModal(!showChangePasswordModal);
  };

  const handleChangeUserPasswordClick = user => {
    setCurrentUser(user);
    toggleShowChangePasswordModal();
  };

  const changePassword = async (formData) => {
    const changedPassword = await changePasswordByAdmin({email: currentUser.email, password: formData.newPassword});
    setCurrentUser({});
    toggleShowChangePasswordModal();
  };

  return (
    <div className='Users'>
      <div className='header'>
        <div className='headerTitle'></div>
        <div className='headerButtons'>
          <Button type='secondary' label={t('add user')} action={toggleShowAddUserModal} />
        </div>
      </div>
      <div className='usersTable'>
        <div className='usersTableHeader'>
          <div className='headerItemMeatball' />
          <div className='headerItem'>{t('e-mail')}</div>
          <div className='headerItem'>{t('first name')}</div>
          <div className='headerItem'>{t('last name')}</div>
          <div className='headerItem'>{t('role')}</div>
        </div>

        {pending && (
          <div className='loaderWrapper'><Loader /></div>
        )}

        {!pending && users.map(user => (
          <UserRow
            key={user.id}
            user={user}
            openEditUserModal={handleEditUserClick}
            refreshUserList={() => getUsers(10, page)}
            rolesOptions={rolesOptions}
            openDeleteUserModal={handleDeleteUserClick}
            openChangePasswordModal={handleChangeUserPasswordClick}
          />
        ))}

        {!pending && !!paginationInfo && !!paginationInfo?.totalPages && (
          <div className='paginationRow'>
            <div className='paginationRowItem'>
              <div
                className={`paginationButton ${paginationInfo.totalPages === 1 || !jumpToPageInput.length
                  ? 'paginationDisabled'
                  : ''
                }`}
                onClick={changePage}
              >
                {t('go to page')}
              </div>
              <input
                type='number'
                className={`paginationInput ${paginationInfo.totalPages === 1 ? 'paginationInputDisabled' : ''}`}
                onChange={e => setJumpToPageInput(e.target.value)}
                onKeyDown={handleChangePageOnEnterPress}
                disabled={paginationInfo.totalPages === 1}
              />
            </div>

            <div className='paginationRowItem'>
              <div
                className={`paginationButton ${paginationInfo.number === 0 ? 'paginationDisabled' : ''}`}
                onClick={changePageDown}
              >
                <ArrowLeftIcon />
              </div>
              <div className='paginationInfo'>
                {t('page')} {paginationInfo.number + 1}/{paginationInfo.totalPages}
              </div>
              <div
                className={`paginationButton ${paginationInfo.number === paginationInfo.totalPages - 1
                  ? 'paginationDisabled'
                  : ''
                }`}
                onClick={changePageUp}
              >
                <ArrowRightIcon />
              </div>
            </div>
          </div>
        )}
      </div>

      {!!showAddUserModal && (
        <Modal
          closeModal={toggleShowAddUserModal}
          title={t('add user')}
          width='320px'
          height='534px'
        >
          <UserForm
            updateUserData={updateNewUserData}
            rolesOptions={rolesOptions}
            primaryButtonAction={addNewUser}
            secondaryButtonAction={toggleShowAddUserModal}
            emailError={emailError}
            setEmailError={setEmailError}
          />
        </Modal>
      )}

      {!!showEditUserModal && (
        <Modal
          closeModal={toggleShowEditUserModal}
          title={t('edit user')}
          width='320px'
          height='436px'
        >
          <UserForm
            updateUserData={updateNewUserData}
            rolesOptions={rolesOptions}
            user={currentUser}
            primaryButtonAction={editUser}
            secondaryButtonAction={toggleShowEditUserModal}
          />
        </Modal>
      )}

      {!!showDeleteUserModal && (
        <ConfirmationModal
          title={t('delete user')}
          itemName={currentUser.firstName + " " + currentUser.lastName}
          closeModal={toggleShowDeleteUserModal}
          primaryButtonAction={removeUser}
        />
      )}

      {!!showChangePasswordModal && (
        <Modal
          closeModal={toggleShowChangePasswordModal}
          title={t('change password')}
          width='310px'
        >
          <ChangePasswordForm
            primaryButtonAction={changePassword}
            secondaryButtonAction={toggleShowChangePasswordModal}
          />
        </Modal>
      )}
    </div>
  );
};

export default Users;
